/**
 * Shared test fixtures for hantavirus.
 *
 * These fixtures are the canonical seed data used by every test suite —
 * unit tests, integration tests, and Playwright E2E. Importing from here
 * (rather than defining ad-hoc literals in each test) keeps fixture
 * shape consistent with the real schema and makes provenance
 * regressions show up everywhere at once.
 *
 * Provenance contract: every numeric field carries `sourceUrl` +
 * `verifiedAt`, matching the public schema invariant.
 */

export const FIXTURE_VERIFIED_AT = "2026-04-12T08:30:00.000Z";

/** The canonical maritime outbreak (MV Hondius) used across the test suite. */
export const mvHondiusOutbreak = {
  id: 1,
  slug: "mv-hondius-2026",
  name: "MV Hondius Outbreak (Antarctic Peninsula)",
  virusStrain: "andes",
  syndrome: "hps" as const,
  status: "active" as const,
  riskLevelWho: "high" as const,
  startedAt: "2026-03-22T14:00:00.000Z",
  resolvedAt: null,
  summary:
    "Cluster of HPS cases aboard the expedition vessel MV Hondius, attributed to Andes virus exposure during a shore landing.",
  summaryLocaleEn: "Cluster of HPS cases aboard the expedition vessel MV Hondius.",
  summaryLocaleAr: "تجمع لحالات متلازمة هانتا الرئوية على متن سفينة الاستكشاف MV Hondius.",
  summaryLocaleFr:
    "Foyer de cas de SPH à bord du navire d'expédition MV Hondius.",
  populationAtRisk: 174,
  primaryCountry: null,
  createdAt: "2026-03-22T14:30:00.000Z",
  updatedAt: "2026-04-10T09:15:00.000Z",
} as const;

/** Case-count time series for the MV Hondius outbreak — drives CFR computation. */
export const mvHondiusCaseCounts = [
  {
    id: 1,
    outbreakId: 1,
    kind: "confirmed" as const,
    value: 23,
    asOf: "2026-04-12T00:00:00.000Z",
    sourceUrl: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
    sourceName: "WHO DON",
    verifiedAt: FIXTURE_VERIFIED_AT,
  },
  {
    id: 2,
    outbreakId: 1,
    kind: "death" as const,
    value: 4,
    asOf: "2026-04-12T00:00:00.000Z",
    sourceUrl: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
    sourceName: "WHO DON",
    verifiedAt: FIXTURE_VERIFIED_AT,
  },
  {
    id: 3,
    outbreakId: 1,
    kind: "recovered" as const,
    value: 11,
    asOf: "2026-04-12T00:00:00.000Z",
    sourceUrl: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
    sourceName: "WHO DON",
    verifiedAt: FIXTURE_VERIFIED_AT,
  },
  {
    id: 4,
    outbreakId: 1,
    kind: "active" as const,
    value: 8,
    asOf: "2026-04-12T00:00:00.000Z",
    sourceUrl: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
    sourceName: "WHO DON",
    verifiedAt: FIXTURE_VERIFIED_AT,
  },
] as const;

/**
 * Expected CFR for the canonical MV Hondius numbers above:
 *   deaths / confirmed = 4 / 23 ≈ 17.39%
 * Use this constant in tests so the assertion can't drift away from
 * the fixture data.
 */
export const mvHondiusExpectedCfr = 17.39;

/** A known-good AIS position for MV Hondius (close to Antarctic Peninsula). */
export const validShipPosition = {
  mmsi: "246456000",
  imo: "9477213",
  name: "MV Hondius",
  callsign: "PBLM",
  lat: -64.7833,
  lon: -62.6167,
  sog: 8.4,
  cog: 213.5,
  heading: 215,
  navStatus: 0,
  receivedAt: "2026-04-12T07:45:00.000Z",
  source: "aisstream" as const,
} as const;

/** A 24-hour history of ship positions used by map seed + replay tests. */
export const mvHondiusPositionHistory = Array.from({ length: 24 }).map((_, i) => ({
  ...validShipPosition,
  lat: -64.7833 + i * 0.01,
  lon: -62.6167 - i * 0.005,
  receivedAt: new Date(
    Date.parse("2026-04-11T07:45:00.000Z") + i * 60 * 60 * 1000,
  ).toISOString(),
}));

/** Sample WHO DON RSS item — drives the WHO scraper integration test. */
export const whoNewsItem = {
  guid: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
  title: "Hantavirus cluster aboard expedition vessel MV Hondius — Antarctic",
  link: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
  pubDate: "2026-04-10T15:00:00.000Z",
  description:
    "WHO has been notified of a cluster of confirmed Hantavirus Pulmonary Syndrome cases among passengers and crew of the expedition vessel MV Hondius.",
  contentSnippet:
    "23 confirmed cases, 4 deaths, 11 recovered. Investigation ongoing in collaboration with Argentinian and Chilean authorities.",
  source: "who-don" as const,
};

/** A ticker item that has expired (more than 7 days old, status: expired). */
export const expiredTickerItem = {
  id: 901,
  outbreakId: 1,
  kind: "advisory" as const,
  headline: "WHO travel advisory issued for Antarctic expedition cruises",
  body: "Initial WHO statement; superseded by April 12 update.",
  link: "https://www.who.int/news/item/2026-03-25-mv-hondius",
  publishedAt: "2026-03-25T11:00:00.000Z",
  expiresAt: "2026-04-01T11:00:00.000Z",
  status: "expired" as const,
};

/** A fresh ticker item (within the 24h window). */
export const freshTickerItem = {
  id: 905,
  outbreakId: 1,
  kind: "case_update" as const,
  headline: "Confirmed cases rise to 23 aboard MV Hondius",
  body: "WHO update: 23 confirmed, 4 deaths, 11 recovered, 8 active. Vessel under quarantine in Ushuaia.",
  link: "https://www.who.int/emergencies/disease-outbreak-news/2026/mv-hondius",
  publishedAt: "2026-04-12T08:30:00.000Z",
  expiresAt: "2026-04-19T08:30:00.000Z",
  status: "live" as const,
};

/** Sample FAQ rows in 3 locales. */
export const faqFixtures = [
  {
    id: 1,
    slug: "what-is-hantavirus",
    question: "What is hantavirus?",
    answer:
      "Hantaviruses are a family of viruses spread mainly by rodents. Different strains cause two distinct diseases: HPS (in the Americas) and HFRS (in Europe and Asia).",
    locale: "en" as const,
    category: "general" as const,
    publishedAt: "2026-01-15T00:00:00.000Z",
  },
  {
    id: 2,
    slug: "what-is-hantavirus",
    question: "ما هو فيروس هانتا؟",
    answer:
      "فيروسات هانتا عائلة من الفيروسات تنتقل بشكل رئيسي عبر القوارض. تسبب السلالات المختلفة مرضين متمايزين: متلازمة هانتا الرئوية في الأمريكتين، والحمى النزفية مع المتلازمة الكلوية في أوروبا وآسيا.",
    locale: "ar" as const,
    category: "general" as const,
    publishedAt: "2026-01-15T00:00:00.000Z",
  },
  {
    id: 3,
    slug: "what-is-hantavirus",
    question: "Qu'est-ce que le hantavirus ?",
    answer:
      "Les hantavirus sont une famille de virus principalement transmis par les rongeurs. Différentes souches provoquent deux maladies distinctes : le SPH dans les Amériques et la FHSR en Europe et en Asie.",
    locale: "fr" as const,
    category: "general" as const,
    publishedAt: "2026-01-15T00:00:00.000Z",
  },
];

/** Subscriber row used by subscribe-confirm flow tests. */
export const pendingSubscriberFixture = {
  id: 42,
  email: "test+confirm@hantavirus.app",
  locale: "en" as const,
  status: "pending" as const,
  confirmToken: "test-confirm-token-do-not-use-in-prod",
  createdAt: "2026-04-12T08:00:00.000Z",
  confirmedAt: null,
};

/** Confirmed subscriber. */
export const confirmedSubscriberFixture = {
  ...pendingSubscriberFixture,
  id: 43,
  email: "test+digest@hantavirus.app",
  status: "confirmed" as const,
  confirmToken: null,
  confirmedAt: "2026-04-12T08:05:00.000Z",
};

/** Admin user fixture for admin-router tests. */
export const adminUserFixture = {
  id: "admin-user-test",
  email: "admin@hantavirus.app",
  name: "Test Admin",
  role: "admin" as const,
  locale: "en" as const,
};

/** Standard test locale list — every locale-aware test should iterate this. */
export const supportedTestLocales = ["en", "ar", "fr"] as const;
export type SupportedTestLocale = (typeof supportedTestLocales)[number];

/** Channel name helpers for SSE tests — must match server's wire format. */
export const sseChannels = {
  outbreak: (slug: string) => `outbreak:${slug}`,
  ship: (mmsi: string) => `ship:${mmsi}`,
  globalTicker: () => "ticker:global",
  ticker: (slug: string) => `ticker:${slug}`,
} as const;

/** Lightweight matcher for case_counts row coming back from the DB. */
export interface CaseCountRow {
  outbreakId: number;
  kind: string;
  value: number;
  sourceUrl: string;
  verifiedAt: string;
}

/** Compute CFR the same way the SQL view does (deaths / confirmed * 100). */
export function expectedCfr(rows: CaseCountRow[]): number | null {
  const deaths = rows.find((r) => r.kind === "death")?.value ?? 0;
  const confirmed = rows.find((r) => r.kind === "confirmed")?.value ?? 0;
  if (confirmed === 0) return null;
  return Math.round((deaths / confirmed) * 100 * 100) / 100;
}
